import { useMutation, useQuery } from '@apollo/client'
import { Circle } from '@mui/icons-material'
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    ListItemText,
    MenuItem,
    Popover,
    Select,
    SelectChangeEvent,
    Stack,
    Typography,
} from '@mui/material'
import { FC, useState } from 'react'
import { GetCandidatesForRosterSql } from 'recruiter/contexts'
import {
    GetRosterColors,
    AddRosterCandidateColors,
    RemoveAllRosterCandidateColors,
} from 'recruiter/graphql/rosters.gql'
import { useBulkActionContext } from 'recruiter/hooks/useBulkActionContext'
import useToast from 'shared/components/Toast/useToast'
import { formatGraphQLError, getPxAsRem } from 'shared/utils'

interface EditColorsProps {
    onClose: () => void
    selectedRosterCandidateIds?: string[]
    isOpen: boolean
    anchorRef: React.RefObject<HTMLButtonElement>
    rosterId?: string
    candidateIds?: string[]
}

const EditColors: FC<EditColorsProps> = ({
    selectedRosterCandidateIds,
    onClose,
    anchorRef,
    isOpen,
    rosterId,
    candidateIds,
}) => {
    const toast = useToast()
    const { setLoading } = useBulkActionContext()
    const { data: colors } = useQuery(GetRosterColors)
    const [updateCandidateColors] = useMutation(AddRosterCandidateColors, {
        refetchQueries: [GetCandidatesForRosterSql],
        onCompleted: () => {
            toast({
                title: 'Success!',
                message: 'Colors added to candidates successfully',
                severity: 'success',
            })
            onClose()
            setLoading(false)
        },
        onError: (error) => {
            toast({
                title: 'Error!',
                message: formatGraphQLError(
                    error,
                    'Failed to add colors to candidates'
                ),
                severity: 'error',
            })
            setLoading(false)
        },
    })
    const [removeAllColors] = useMutation(RemoveAllRosterCandidateColors, {
        refetchQueries: [GetCandidatesForRosterSql],
        onCompleted: () => {
            toast({
                title: 'Success!',
                message: 'All colors removed from candidates successfully',
                severity: 'success',
            })
            onClose()
            setLoading(false)
        },
        onError: (error) => {
            toast({
                title: 'Error!',
                message: formatGraphQLError(
                    error,
                    'Failed to remove colors from candidates'
                ),
                severity: 'error',
            })
            setLoading(false)
        },
    })

    const [selectedColors, setSelectedColors] = useState<string[]>([])
    const [removeAll, setRemoveAll] = useState<boolean>(false)

    const colorChoices = colors?.rosterColors
        ? [...colors.rosterColors].sort((a, b) => a.order - b.order)
        : []

    const handleColorChange = (event: SelectChangeEvent<string[]>) => {
        if (typeof event.target.value !== 'string') {
            setSelectedColors(event.target.value)
            // If colors are selected, uncheck "Remove All"
            if (event.target.value.length > 0) {
                setRemoveAll(false)
            }
        }
    }

    const handleRemoveAllChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const checked = event.target.checked
        setRemoveAll(checked)
        if (checked) {
            // If "Remove All" is checked, clear selected colors
            setSelectedColors([])
        }
    }

    const renderColorChip = (colorId: string) => {
        const color = colorChoices.find((c) => c.id === colorId)
        if (!color) return null

        return (
            <Box
                key={colorId}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    padding: '2px 6px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1,
                    fontSize: '0.75rem',
                }}
            >
                <Circle
                    sx={{
                        width: 12,
                        height: 12,
                        fill: color.hexCode,
                    }}
                />
                {color.value}
            </Box>
        )
    }

    const renderSelectValue = (selected: string[]) => {
        if (selected.length === 0) {
            return (
                <Typography color="text.secondary">
                    Select colors to add
                </Typography>
            )
        }

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                }}
            >
                {selected.map(renderColorChip)}
            </Box>
        )
    }

    const handleSave = () => {
        const idVariables = candidateIds?.length
            ? { rosterId, candidateIds }
            : { rosterCandidateIds: selectedRosterCandidateIds }

        if (removeAll) {
            setLoading(true)
            removeAllColors({ variables: idVariables })
        } else if (selectedColors.length > 0) {
            setLoading(true)
            updateCandidateColors({
                variables: {
                    ...idVariables,
                    rosterColorIds: selectedColors,
                },
            })
        } else {
            onClose()
        }
    }

    const handleClose = () => {
        // Reset state when closing
        setSelectedColors([])
        setRemoveAll(false)
        onClose()
    }

    const isSaveDisabled = !removeAll && selectedColors.length === 0

    return (
        <Popover
            open={isOpen}
            anchorEl={anchorRef.current}
            onClose={handleClose}
            slotProps={{
                paper: {
                    sx: {
                        marginTop: getPxAsRem(32),
                        minWidth: getPxAsRem(280),
                        padding: getPxAsRem(16),
                    },
                },
            }}
        >
            <Stack spacing={2}>
                {/* Color Selection */}
                <Box>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ marginBottom: getPxAsRem(8) }}
                    >
                        Add Colors:
                    </Typography>
                    <Select
                        multiple
                        value={selectedColors}
                        onChange={handleColorChange}
                        disabled={removeAll}
                        displayEmpty
                        fullWidth
                        size="small"
                        renderValue={renderSelectValue}
                    >
                        {colorChoices.map((color) => (
                            <MenuItem
                                key={color.hexCode}
                                value={color.id}
                                dense
                            >
                                <Checkbox
                                    checked={selectedColors.includes(color.id)}
                                    size="small"
                                />
                                <ListItemText
                                    primary={color.value}
                                    sx={{ marginLeft: getPxAsRem(8) }}
                                />
                                <Circle
                                    sx={{
                                        fill: color.hexCode,
                                        marginLeft: getPxAsRem(12),
                                        width: 16,
                                        height: 16,
                                    }}
                                />
                            </MenuItem>
                        ))}
                    </Select>
                </Box>

                <Divider />

                {/* Remove All Option */}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={removeAll}
                            onChange={handleRemoveAllChange}
                            size="small"
                        />
                    }
                    label={
                        <Typography variant="body1">
                            Remove All Colors
                        </Typography>
                    }
                />

                {/* Action Buttons */}
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleSave}
                        disabled={isSaveDisabled}
                    >
                        Save
                    </Button>
                </Stack>
            </Stack>
        </Popover>
    )
}

export default EditColors
